<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :emptyCollectionMessage="emptyMessage"
      :inDetailsAsidePanel="true"
      :itemType="osMetadata[os].itemType"
      :selectionItemText="selectionItemText"
      :showBound="false"
      :showSearchForm="true"
      :useDropDownFilterComponent="showOsFilter"
      :viewport="viewport"
    >
      <template
        v-if="showEmptyState"
        v-slot:emptyState
      >
        <NoSearchResults
          ctaText="create a new command."
          instructions="If you didn't find the command you were looking for,"
          title="No Results"
          @click="handleNewCommandClick"
        />
      </template>
    </ItemTable>
  </div>
</template>
<script>
import {
  CommandTemplatesConsts,
  FilterComponentConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { NoSearchResults } from '@jumpcloud/ui-components';
import { extend } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import CommandConstants from 'client/util/Constants/CommandConstants';
import CommandTemplateConfigureButton from 'client/components/ItemTable/BodyComponents/CommandTemplateConfigureButton.vue';
import CommandTemplateDescription from 'client/components/ItemTable/BodyComponents/CommandTemplateDescription.vue';
import CommandTemplateName from 'client/components/ItemTable/BodyComponents/CommandTemplateName.vue';
import CommandTemplateOS from 'client/components/ItemTable/BodyComponents/CommandTemplateOS.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import MspState from 'client/components/models/MspState';
import Util from 'client/util/Util';

const { runAsUsers } = CommandConstants;
// This is so we can set fields for a new command
const commandModel = StoreModelMap[ItemTypeConsts.command];
const { types: osTypes } = CommandTemplatesConsts;

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const validator = [osTypes.linux, osTypes.mac, osTypes.windows];

export default {
  name: 'CommandTemplateItemTable',

  components: {
    ItemTable,
    NoSearchResults,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTablePropsMixin,
  ],

  EventHelper,

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.commandTemplateWindows,
    },
    os: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(validator).includes(value);
      },
    },
  },

  data() {
    return {
      FilterComponentConsts,
      osMetadata: {
        [osTypes.linux]: {
          itemType: ItemTypeConsts.commandTemplateLinux,
          storeModule: StoreModelMap[ItemTypeConsts.commandTemplateLinux],
        },
        [osTypes.mac]: {
          itemType: ItemTypeConsts.commandTemplateMacOs,
          storeModule: StoreModelMap[ItemTypeConsts.commandTemplateMacOs],
        },
        [osTypes.windows]: {
          itemType: ItemTypeConsts.commandTemplateWindows,
          storeModule: StoreModelMap[ItemTypeConsts.commandTemplateWindows],
        },
      },
    };
  },

  computed: {
    ...mapState(commandModel, ['item']),

    ...mapState(settingsModule, [
      'orgId',
      'user',
    ]),

    columns() {
      return [
        {
          bodyComponent: CommandTemplateName,
          bodyComponentProps: {
            itemType: this.itemType,
          },
          classNames: {
            body: [this.$style.commandTemplateNameColumn],
          },
          headingText: 'Name',
          sortBy: 'name',
        },
        {
          bodyComponent: CommandTemplateDescription,
          bodyComponentProps: {
            itemType: this.itemType,
          },
          classNames: {
            body: [this.$style.scriptColumn],
            heading: [this.$style.scriptColumn],
          },
          sortBy: 'description',
          headingText: 'Description',
        },
        {
          bodyComponent: CommandTemplateOS,
          headingText: 'OS',
          sortBy: 'type',
        },
        {
          bodyComponent: CommandTemplateConfigureButton,
          bodyComponentProps: {
            handleClick: this.handleConfigureClick,
            buttonText: 'configure',
          },
        },
      ];
    },

    // Note: emptyMessage will only show up if the initial call to get templates
    // completely returns 0 results. Does not show this if the table is searched
    // and there are not results...
    emptyMessage() {
      return 'No Command Templates are available.';
    },

    showEmptyState() {
      return this.getTotalCount <= 0;
    },

    showOsFilter() {
      return MspState.isAnMsp ? FilterComponentConsts.CommandTemplatesDropDownFilter : '';
    },
  },

  beforeCreate() {
    const storeModel = StoreModuleMap[this.$options.propsData.itemType];
    if (!storeModel) {
      throw new Error('Could not find a store model');
    }

    /**
     * Extend the computed properties and methods here, before the component is
     * created so that the namespace argument to mapActions() and mapGetters()
     * can be set dynamically from the props passed to the ItemTable component.
     * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
     */
    extend(
      this.$options.computed,
      mapGetters(storeModel, {
        getTotalCount: 'getTotalCount',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(storeModel, {
        setProviderId: 'setProviderId',
        setOrganizationId: 'setOrganizationId',
      }),
    );
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(commandModel, ['setItemField']),

    handleConfigureClick(item) {
      this.$options.EventHelper.triggerCommandTemplatesClicked({
        templateName: item.name,
      });
      this.handleNewCommandClick();
      this.setItemField({ key: 'basedOnTemplate', value: item.name });
      this.setItemField({ key: 'name', value: item.name });
      this.setItemField({ key: 'commandType', value: item.type });
      this.setItemField({ key: 'command', value: item.command });
      if (item.type === osTypes.windows) {
        this.setItemField({ key: 'shell', value: 'powershell' });
      }
      if (item.type !== osTypes.windows) {
        this.setItemField({ key: 'user', value: runAsUsers.root });
      }
    },

    handleMounted() {
      this.setOrganizationId(this.orgId);
      if (MspState.isAnMsp) {
        this.setProviderId(this.user.provider);
      }
    },

    handleNewCommandClick() {
      Util.redirect(`${ItemTypeRoutes[ItemTypeConsts.command]}/new`);
    },
  },
};
</script>

<style module>
.commandTemplateNameColumn {
  display: flex;
  flex-wrap: wrap;
  max-width: 160px;
}

.scriptColumn {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
